import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  FiUploadCloud,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiImage,
  FiVideo,
  FiFile,
  FiTrash2,
  FiRefreshCw,
} from 'react-icons/fi';
import useCmsStore from '../../../services/useCmsStore';
import './MediaLibrary.css';

const ACCEPTED_TYPES = {
  image: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'image/svg+xml': ['.svg'],
  },
  video: {
    'video/mp4': ['.mp4'],
    'video/webm': ['.webm'],
    'video/ogg': ['.ogg'],
    'video/quicktime': ['.mov'],
  },
  document: {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/plain': ['.txt'],
    'text/csv': ['.csv'],
  },
};

const MediaUploader = ({
  onComplete,
  onCancel,
  acceptedTypes = ['image', 'video', 'document'],
  maxFiles = 20,
  maxSize = 50 * 1024 * 1024, // 50MB
}) => {
  const { uploadMedia } = useCmsStore();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Build accepted file types object
  const accept = acceptedTypes.reduce((acc, type) => {
    return { ...acc, ...ACCEPTED_TYPES[type] };
  }, {});

  // Handle file drop
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Add accepted files
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      status: 'pending',
      progress: 0,
      error: null,
    }));

    // Add rejected files with errors
    const rejectedFilesWithErrors = rejectedFiles.map(({ file, errors }) => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      preview: null,
      status: 'error',
      progress: 0,
      error: errors.map(e => e.message).join(', '),
    }));

    setFiles(prev => [...prev, ...newFiles, ...rejectedFilesWithErrors]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    maxSize,
    multiple: true,
  });

  // Remove file from list
  const removeFile = (id) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  // Clear all files
  const clearAll = () => {
    files.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setFiles([]);
  };

  // Retry failed upload
  const retryUpload = async (id) => {
    const fileToRetry = files.find(f => f.id === id);
    if (!fileToRetry || fileToRetry.status !== 'error') return;

    setFiles(prev =>
      prev.map(f =>
        f.id === id ? { ...f, status: 'uploading', progress: 0, error: null } : f
      )
    );

    try {
      const uploaded = await uploadMedia(fileToRetry.file);
      setFiles(prev =>
        prev.map(f =>
          f.id === id ? { ...f, status: 'completed', progress: 100, uploadedData: uploaded } : f
        )
      );
    } catch (error) {
      setFiles(prev =>
        prev.map(f =>
          f.id === id
            ? { ...f, status: 'error', error: error.message || 'Upload failed' }
            : f
        )
      );
    }
  };

  // Upload all pending files
  const uploadAll = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setUploading(true);

    const uploadedMedia = [];

    for (const fileItem of pendingFiles) {
      // Update status to uploading
      setFiles(prev =>
        prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'uploading' } : f
        )
      );

      try {
        // Simulate progress (actual progress would come from axios onUploadProgress)
        const progressInterval = setInterval(() => {
          setFiles(prev =>
            prev.map(f =>
              f.id === fileItem.id && f.status === 'uploading'
                ? { ...f, progress: Math.min(f.progress + 10, 90) }
                : f
            )
          );
        }, 200);

        const uploaded = await uploadMedia(fileItem.file);

        clearInterval(progressInterval);

        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id
              ? { ...f, status: 'completed', progress: 100, uploadedData: uploaded }
              : f
          )
        );

        uploadedMedia.push(uploaded);
      } catch (error) {
        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id
              ? { ...f, status: 'error', error: error.message || 'Upload failed' }
              : f
          )
        );
      }
    }

    setUploading(false);

    // If all uploads successful, call onComplete
    const allCompleted = files.every(f => f.status === 'completed');
    if (allCompleted && uploadedMedia.length > 0) {
      onComplete(uploadedMedia);
    }
  };

  // Get file icon
  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return FiImage;
    if (file.type.startsWith('video/')) return FiVideo;
    return FiFile;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const completedCount = files.filter(f => f.status === 'completed').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <div className="media-uploader">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`media-dropzone ${isDragActive ? 'active' : ''}`}
      >
        <input {...getInputProps()} />
        <FiUploadCloud size={48} />
        {isDragActive ? (
          <p>Drop files here...</p>
        ) : (
          <>
            <p>Drag & drop files here, or click to select</p>
            <span>
              Max {maxFiles} files, up to {maxSize / (1024 * 1024)}MB each
            </span>
          </>
        )}
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="media-upload-list">
          <div className="media-upload-list-header">
            <span>
              {files.length} file{files.length > 1 ? 's' : ''} selected
              {completedCount > 0 && ` (${completedCount} uploaded)`}
              {errorCount > 0 && ` (${errorCount} failed)`}
            </span>
            <button className="btn-clear-all" onClick={clearAll}>
              Clear all
            </button>
          </div>

          <div className="media-upload-items">
            {files.map(fileItem => {
              const Icon = getFileIcon(fileItem.file);
              return (
                <div
                  key={fileItem.id}
                  className={`media-upload-item status-${fileItem.status}`}
                >
                  <div className="media-upload-preview">
                    {fileItem.preview ? (
                      <img src={fileItem.preview} alt="" />
                    ) : (
                      <div className="media-upload-icon">
                        <Icon size={24} />
                      </div>
                    )}
                  </div>
                  <div className="media-upload-info">
                    <span className="media-upload-name">{fileItem.file.name}</span>
                    <span className="media-upload-size">
                      {formatFileSize(fileItem.file.size)}
                    </span>
                    {fileItem.error && (
                      <span className="media-upload-error">
                        <FiAlertCircle size={12} />
                        {fileItem.error}
                      </span>
                    )}
                    {fileItem.status === 'uploading' && (
                      <div className="media-upload-progress">
                        <div
                          className="media-upload-progress-bar"
                          style={{ width: `${fileItem.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="media-upload-actions">
                    {fileItem.status === 'pending' && (
                      <button onClick={() => removeFile(fileItem.id)} title="Remove">
                        <FiX size={16} />
                      </button>
                    )}
                    {fileItem.status === 'completed' && (
                      <span className="media-upload-success">
                        <FiCheck size={16} />
                      </span>
                    )}
                    {fileItem.status === 'error' && (
                      <>
                        <button onClick={() => retryUpload(fileItem.id)} title="Retry">
                          <FiRefreshCw size={16} />
                        </button>
                        <button onClick={() => removeFile(fileItem.id)} title="Remove">
                          <FiTrash2 size={16} />
                        </button>
                      </>
                    )}
                    {fileItem.status === 'uploading' && (
                      <span className="media-upload-spinner" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="media-uploader-actions">
        <button className="btn-back" onClick={onCancel}>
          Back to Library
        </button>
        {pendingCount > 0 && (
          <button
            className="btn-upload-all"
            onClick={uploadAll}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <span className="btn-spinner" />
                Uploading...
              </>
            ) : (
              <>
                <FiUploadCloud size={16} />
                Upload {pendingCount} file{pendingCount > 1 ? 's' : ''}
              </>
            )}
          </button>
        )}
        {completedCount > 0 && completedCount === files.length && (
          <button
            className="btn-done"
            onClick={() => onComplete(files.filter(f => f.uploadedData).map(f => f.uploadedData))}
          >
            <FiCheck size={16} />
            Done
          </button>
        )}
      </div>
    </div>
  );
};

export default MediaUploader;
